import React, { useState, useEffect, useRef } from 'react';
import { Terminal, Trash2, Download } from 'lucide-react';
import { logger, type LogEntry, LogLevel } from '../../utils/logger';

type ConsoleViewerProps = {
  className?: string;
};

export default function ConsoleViewer({ className = '' }: ConsoleViewerProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Get initial logs
    setLogs(logger.getLogs());

    // Subscribe to new logs
    const unsubscribe = logger.subscribe((entry) => {
      setLogs(prev => [...prev, entry]);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const getLogColor = (level: string) => {
    switch (level) {
      case LogLevel.ERROR:
        return 'text-red-600';
      case LogLevel.WARN:
        return 'text-yellow-600';
      case LogLevel.INFO:
        return 'text-blue-600';
      case LogLevel.DEBUG:
        return 'text-gray-600';
      default:
        return 'text-gray-900';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const formatData = (data: any): string => {
    if (!data) return '';
    try {
      return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  const handleClear = () => {
    logger.clear();
    setLogs([]);
  };

  const handleDownload = () => {
    const content = logs.map(log => 
      `[${formatTimestamp(log.timestamp)}] ${log.level.toUpperCase()}: ${log.message}${
        log.data ? '\n' + formatData(log.data) : ''
      }${log.source ? ' (Source: ' + log.source + ')' : ''}`
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `console-logs-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredLogs = logs.filter(log => 
    log.message.toLowerCase().includes(filter.toLowerCase()) ||
    (log.source?.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Filter logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:border-[#212d52] focus:ring-2 focus:ring-[#212d52]/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded border-gray-300 text-[#212d52] focus:ring-[#212d52]"
            />
            Auto-scroll
          </label>
          <button
            onClick={handleClear}
            className="p-1.5 text-gray-600 hover:text-gray-900 transition-colors"
            title="Clear logs"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            className="p-1.5 text-gray-600 hover:text-gray-900 transition-colors"
            title="Download logs"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div 
        ref={containerRef}
        className="h-[300px] overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm"
      >
        {filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Terminal className="w-5 h-5 mr-2" />
            No logs to display
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {filteredLogs.map((log, index) => (
              <div key={index} className="space-y-1">
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 flex-shrink-0">
                    {formatTimestamp(log.timestamp)}
                  </span>
                  <span className={`font-medium ${getLogColor(log.level)}`}>
                    {log.level.toUpperCase()}
                  </span>
                  <span className="text-gray-900 break-all">{log.message}</span>
                </div>
                {log.data && (
                  <pre className="ml-[140px] text-xs text-gray-600 whitespace-pre-wrap break-all">
                    {formatData(log.data)}
                  </pre>
                )}
                {log.source && (
                  <div className="ml-[140px] text-xs text-gray-500">
                    Source: {log.source}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}